import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { AppShell } from "@/components/layout/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { listAudits, type AuditRow } from "@/lib/api";
import { formatTimestamp } from "@/lib/utils";
import { AuditDetailModal } from "@/components/AuditDetailModal";

const PAGE_SIZE = 50;

const ACTIONS: { value: string; label: string }[] = [
  { value: "", label: "All actions" },
  { value: "update_user_data", label: "Update User Data" },
  { value: "send_mail", label: "Send Mail" },
  { value: "grant_items", label: "Grant Items" },
  { value: "ban", label: "Ban" },
  { value: "revoke_bans", label: "Revoke Bans" },
  { value: "flag_rescanned", label: "Rescan Flags" },
];

const ACTION_VARIANT: Record<string, "default" | "secondary" | "destructive" | "outline" | "success"> = {
  update_user_data: "secondary",
  send_mail: "outline",
  grant_items: "outline",
  ban: "destructive",
  revoke_bans: "success",
};

export function AuditRoute() {
  const [params, setParams] = useSearchParams();
  const [openId, setOpenId] = useState<number | null>(null);

  const playerFilter = params.get("playfabId") ?? "";
  const adminFilter = params.get("admin") ?? "";
  const actionFilter = params.get("action") ?? "";
  const offset = Number(params.get("offset") ?? 0);

  const query = useQuery({
    queryKey: ["audit", playerFilter, adminFilter, actionFilter, offset],
    queryFn: () =>
      listAudits({
        playfabId: playerFilter || undefined,
        admin: adminFilter || undefined,
        action: actionFilter || undefined,
        limit: PAGE_SIZE,
        offset,
      }),
  });

  function setParam(k: string, v: string | undefined) {
    const next = new URLSearchParams(params);
    if (!v) next.delete(k);
    else next.set(k, v);
    next.delete("offset");
    setParams(next, { replace: true });
  }

  function setOffset(o: number) {
    const next = new URLSearchParams(params);
    if (o <= 0) next.delete("offset");
    else next.set("offset", String(o));
    setParams(next, { replace: true });
  }

  function clearFilters() {
    setParams({}, { replace: true });
  }

  const total = query.data?.total ?? 0;
  const rows = query.data?.rows ?? [];

  return (
    <AppShell title="Audit Log" subtitle={`${total} entries`}>
      <div className="space-y-4 max-w-6xl">
        {/* Filters */}
        <div className="flex flex-wrap items-center gap-2">
          <Input
            value={playerFilter}
            onChange={(e) => setParam("playfabId", e.target.value)}
            placeholder="Filter by UID"
            className="w-64"
          />
          <Input
            value={adminFilter}
            onChange={(e) => setParam("admin", e.target.value)}
            placeholder="Filter by admin Discord ID"
            className="w-64"
          />
          <select
            value={actionFilter}
            onChange={(e) => setParam("action", e.target.value)}
            className="h-9 rounded-md border border-input bg-background px-3 text-sm"
          >
            {ACTIONS.map((a) => (
              <option key={a.value} value={a.value}>
                {a.label}
              </option>
            ))}
          </select>
          {(playerFilter || adminFilter || actionFilter) && (
            <Button variant="ghost" size="sm" onClick={clearFilters}>
              Clear
            </Button>
          )}
        </div>

        {query.error && (
          <Alert variant="destructive">
            <AlertDescription>{String(query.error)}</AlertDescription>
          </Alert>
        )}

        <div className="rounded-md border border-border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-xs text-muted-foreground">
              <tr>
                <th className="px-3 py-2 text-left w-48">When</th>
                <th className="px-3 py-2 text-left">Admin</th>
                <th className="px-3 py-2 text-left">Action</th>
                <th className="px-3 py-2 text-left">Target</th>
                <th className="px-3 py-2 text-left">Summary</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {query.isLoading
                ? Array.from({ length: 5 }).map((_, i) => (
                    <tr key={i}>
                      {Array.from({ length: 6 }).map((_, j) => (
                        <td key={j} className="px-3 py-2">
                          <Skeleton className="h-4 w-24" />
                        </td>
                      ))}
                    </tr>
                  ))
                : rows.length === 0
                  ? (
                    <tr>
                      <td colSpan={6} className="px-3 py-6 text-center text-xs text-muted-foreground">
                        No audit entries.
                      </td>
                    </tr>
                  )
                  : rows.map((row: AuditRow) => (
                      <tr key={row.id} className="border-t border-border/50 hover:bg-accent/30">
                        <td className="px-3 py-2 text-xs text-muted-foreground whitespace-nowrap">
                          {formatTimestamp(row.ts)}
                        </td>
                        <td className="px-3 py-2 text-xs">
                          <span className="text-foreground font-medium">{row.admin_name}</span>
                          <span className="text-muted-foreground/70 ml-1.5 text-[10px]">
                            {row.admin_discord}
                          </span>
                        </td>
                        <td className="px-3 py-2">
                          <Badge variant={ACTION_VARIANT[row.action] ?? "secondary"} className="text-[10px]">
                            {row.action}
                          </Badge>
                        </td>
                        <td className="px-3 py-2 text-xs">
                          {row.target_playfab ? (
                            <Link
                              to={`/players/${row.target_playfab}`}
                              className="text-blue-300 hover:underline"
                              title={row.target_playfab}
                            >
                              {row.target_label ?? row.target_playfab.slice(0, 8) + "…"}
                            </Link>
                          ) : (
                            "—"
                          )}
                        </td>
                        <td className="px-3 py-2 text-xs text-muted-foreground truncate max-w-md" title={row.summary}>
                          {row.summary}
                        </td>
                        <td className="px-3 py-2 text-right">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setOpenId(row.id)}
                            disabled={!row.blob_key && !row.metadata}
                            className="h-7 px-2 text-xs"
                          >
                            Details
                          </Button>
                        </td>
                      </tr>
                    ))}
            </tbody>
          </table>
        </div>

        {total > PAGE_SIZE && (
          <div className="flex items-center justify-between">
            <span className="text-xs text-muted-foreground">
              Showing {offset + 1}–{Math.min(offset + PAGE_SIZE, total)} of {total}
            </span>
            <div className="flex gap-2">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
                disabled={offset === 0}
              >
                ← Prev
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setOffset(offset + PAGE_SIZE)}
                disabled={offset + PAGE_SIZE >= total}
              >
                Next →
              </Button>
            </div>
          </div>
        )}
      </div>

      {openId !== null && (
        <AuditDetailModal
          row={rows.find((r) => r.id === openId) ?? null}
          onClose={() => setOpenId(null)}
        />
      )}
    </AppShell>
  );
}
